import React, { useState } from 'react';
import { AlertTriangle, Calendar, Check, Loader2, Clock } from 'lucide-react';
import { addDeadlineToCalendar } from '../../services/emailService';

const DeadlineBanner = ({ emailId, deadlines, caseId, caseName }) => {
  const [addingIdx, setAddingIdx] = useState(null);
  const [addedIdxs, setAddedIdxs] = useState(new Set());

  if (!deadlines || deadlines.length === 0) return null;

  let parsedDeadlines = deadlines;
  if (typeof deadlines === 'string') {
    try { parsedDeadlines = JSON.parse(deadlines); } catch { return null; }
  }
  if (!Array.isArray(parsedDeadlines) || parsedDeadlines.length === 0) return null;

  const handleAddToCalendar = async (dl, idx) => {
    setAddingIdx(idx);
    try {
      await addDeadlineToCalendar(emailId, {
        deadline_date: dl.date,
        deadline_description: dl.description || `Deadline: ${dl.date}`,
        case_id: caseId || null,
        case_name: caseName || null,
      });
      setAddedIdxs(prev => new Set([...prev, idx]));
    } catch (err) {
      console.error('Failed to add to calendar:', err);
      alert('Failed to add deadline to calendar');
    } finally {
      setAddingIdx(null);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T12:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  const daysUntil = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T12:00:00');
      const now = new Date();
      const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
      if (diff < 0) return { text: `${Math.abs(diff)} days overdue`, urgent: true };
      if (diff === 0) return { text: 'TODAY', urgent: true };
      if (diff === 1) return { text: 'Tomorrow', urgent: true };
      if (diff <= 3) return { text: `${diff} days away`, urgent: true };
      if (diff <= 7) return { text: `${diff} days away`, urgent: false };
      return { text: `${diff} days away`, urgent: false };
    } catch { return { text: '', urgent: false }; }
  };

  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-red-500/20">
        <AlertTriangle className="w-4 h-4 text-red-400" />
        <span className="text-sm font-medium text-red-300">
          Deadline{parsedDeadlines.length > 1 ? 's' : ''} Detected
        </span>
      </div>
      <div className="divide-y divide-red-500/10">
        {parsedDeadlines.map((dl, idx) => {
          const timing = daysUntil(dl.date);
          const isAdded = addedIdxs.has(idx);
          return (
            <div key={idx} className="flex items-center gap-3 px-4 py-3">
              <Calendar className={`w-5 h-5 flex-shrink-0 ${timing.urgent ? 'text-red-400' : 'text-amber-400'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white">{formatDate(dl.date)}</span>
                  {timing.text && (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      timing.urgent ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'
                    }`}>
                      {timing.text}
                    </span>
                  )}
                  {dl.confidence >= 0.8 && (
                    <span className="px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded text-[10px]">High confidence</span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5 truncate">{dl.description}</p>
              </div>
              {isAdded ? (
                <span className="flex items-center gap-1 text-xs text-green-400">
                  <Check className="w-3.5 h-3.5" /> Added
                </span>
              ) : (
                <button
                  onClick={() => handleAddToCalendar(dl, idx)}
                  disabled={addingIdx === idx}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-xs font-medium text-red-300 hover:text-red-200 transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  {addingIdx === idx ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <><Calendar className="w-3.5 h-3.5" /> Add to Calendar</>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeadlineBanner;
